import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Activity, Code2, ExternalLink, FileText, FolderTree, LayoutDashboard, LogOut, Menu, Newspaper, Rss, SearchCheck, Settings, Workflow } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { api } from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Inicio", icon: LayoutDashboard, end: true, id: "inicio" },
  { to: "/admin/noticias", label: "Noticias", icon: Newspaper, id: "noticias" },
  { to: "/admin/automatizaciones", label: "Automatizaciones", icon: Workflow, id: "automatizaciones" },
  { to: "/admin/fuentes", label: "Fuentes", icon: Rss, id: "fuentes" },
  { to: "/admin/secciones", label: "Secciones", icon: FolderTree, id: "secciones" },
  { to: "/admin/paginas", label: "Páginas", icon: FileText, id: "paginas" },
  { to: "/admin/codigos", label: "Códigos", icon: Code2, id: "codigos" },
  { to: "/admin/seo", label: "SEO y GEO", icon: SearchCheck, id: "seo" },
  { to: "/admin/actividad", label: "Actividad", icon: Activity, id: "actividad" },
  { to: "/admin/ajustes", label: "Ajustes", icon: Settings, id: "ajustes" },
];

const Sidebar = ({ user, onNavigate }) => {
  const navigate = useNavigate();
  const logout = () => api.post("/auth/logout").finally(() => navigate("/admin/login", { replace: true }));
  return (
    <div className="flex h-full flex-col bg-ink text-slate-300">
      <div className="px-6 pt-7 pb-8">
        <p className="font-display italic text-gold text-lg -mb-1.5">El</p>
        <p className="font-display font-black text-3xl text-white tracking-tight">Noticiero</p>
        <p className="kicker text-[10px] text-slate-500 mt-2">Redacción</p>
      </div>
      <nav className="flex-1 px-3 space-y-0.5">
        {NAV.map(({ to, label, icon: Icon, end, id }) => (
          <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`admin-nav-${id}`}
            className={({ isActive }) => `relative flex items-center gap-3 px-3 py-2.5 text-[14.5px] transition-colors ${isActive ? "bg-white/10 text-white before:absolute before:left-0 before:inset-y-1.5 before:w-[3px] before:bg-gold" : "hover:bg-white/5 hover:text-white"}`}>
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-1">
        <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2.5 text-[14.5px] hover:bg-white/5 hover:text-white" data-testid="admin-view-site-link">
          <ExternalLink className="h-4 w-4" /> Ver la web
        </a>
        <div className="flex items-center gap-3 px-3 py-3">
          {user.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full" /> : <span className="h-8 w-8 rounded-full bg-primary grid place-items-center text-white text-sm font-semibold">{(user.name || user.email)[0]}</span>}
          <div className="min-w-0 flex-1">
            <p className="text-sm text-white truncate" data-testid="admin-user-name">{user.name}</p>
            <p className="text-xs text-slate-500 truncate">{user.email}</p>
          </div>
          <button onClick={logout} aria-label="Cerrar sesión" className="p-1.5 hover:text-white" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};

export const AdminLayout = ({ user, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#F4F3EE] dark:bg-background lg:pl-64" data-testid="admin-layout">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 z-40"><Sidebar user={user} /></aside>
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between h-14 px-4 bg-ink text-white">
        <p className="font-display font-black text-xl">El Noticiero <span className="kicker text-[9px] text-gold ml-1">Redacción</span></p>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button aria-label="Abrir menú" data-testid="admin-mobile-menu-btn"><Menu className="h-6 w-6" /></button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 border-0">
            <SheetTitle className="sr-only">Menú de la redacción</SheetTitle>
            <Sidebar user={user} onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </header>
      <main className="px-5 sm:px-8 lg:px-12 py-8 lg:py-10 max-w-[1440px]">{children}</main>
    </div>
  );
};
